/**
 * AddDemandResponseDialog component renders a modal dialog for scheduling a power curtail operation.
 * It includes a header with a title and a close button, and a form for selecting the scheduling options.
 *
 * @component
 * @example
 * return (
 *   <AddDemandResponseDialog />
 * )
 *
 * @returns {JSX.Element} The rendered AddDemandResponseDialog component.
 */
import React, {
  Component,
  useEffect,
  useState,
} from "react";
import {
  StyledIconButton,
  PrimaryButton,
  SecondaryButton,
  StyledButtonGroup,
  StyledToggleButton,
  StyledToggleButtonGroup,
  StyledRadio,
} from "../../../components/StyledComponents/Buttons/AuradineButtons.js";

import { parse, formatISO, isDate } from "date-fns";
import {
  DateTimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CheckIcon from "@mui/icons-material/Check";
import { StyledDialog } from "../../../components/StyledComponents/Dialog/Dialog.js";
import { useTheme } from "../../../ThemeContext/ThemeContext.js";
import {
  H6HeadlineSmall,
  H8TitleMedium,
  LabelLarge,
  BodyMedium,
} from "../../../components/StyledComponents/Typography/Typography.tsx";
import {
  Autocomplete,
  DialogActions,
  Box,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import useAppContextProvider from "../../../AppContext/useAppContextProvider.js";
import { Controller, useForm } from "react-hook-form";
import { format } from "date-fns";
import dayjs from "dayjs";
import {
  StyledTextField,
  StyledPaper,
  StyledFormControl,
} from "../../../components/StyledComponents/Inputs/Inputs.js";
import PlaceHolderButton from "../../../components/PlaceHolderButton/PlaceHolderButton.js";
import useMinerPageContext from "../../context/useMinerPageContext.js";
import useSiteAndGroupTreeContext from "../../context/useSiteAndGroupTreeContext.js";
import { useSiteCommands } from "../../queries/useSiteCommands.ts";
import { useHistory } from "react-router-dom";
import { set } from "lodash";
import { se } from "date-fns/locale";

const UpgradeMinersDialogHeader = (props) => {
  const { reset } = props;
  const { colors } = useTheme();
  const { setUpgradeModalOpen } = useMinerPageContext();
  return (
    <Box
      display={"flex"}
      flexDirection={"row"}
      justifyContent={"space-between"}
      flexGrow={1}
      padding={"1rem 0"}
    >
      <H6HeadlineSmall>Upgrade miners</H6HeadlineSmall>
      <StyledIconButton 
        colors={colors}
        onClick={() => {
          setUpgradeModalOpen(false);
          reset();
        }}
      >
        <CloseIcon />
      </StyledIconButton>
    </Box>
  );
};

const UpgradeMinersModal = (props) => {
  const { refetch, setTableKey } = props;
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const { colors, currentTheme } = useTheme();
  const {
    upgradeModalOpen,
    setUpgradeModalOpen,
    selectedSerials,
    groupFilters,
    setJobId,
    setStepperOpen,
  } = useMinerPageContext();
  const { siteSelectedName, groupSelectedName } =
    useSiteAndGroupTreeContext();
  const { mutate: sendSiteCommands } = useSiteCommands();
  const navigate = useHistory();

  const defaultStartTime = dayjs().add(10, "minute");

  const {
    watch,
    control,
    handleSubmit,
    setValue,
    reset,
    trigger,
    clearErrors,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    shouldUnregister: true,
    defaultValues: {
      upgradeType: "default",
      startTime: defaultStartTime,
      operationWindow: 120,
      urlOrLatest: "latest",
      url: "",
    },
  });

  // const formatToISO = (dateString) => {
  //   // Parse the input date string
  //   if (isDate(dateString)) {
  //     return formatISO(dateString);
  //   }

  //   // If the input is a string, parse it
  //   if (typeof dateString === "string") {
  //     const parsedDate = parse(dateString, "MM/dd/yyyy hh:mm a", new Date());
  //     return formatISO(parsedDate);
  //   }
  // };

  const onsubmit = (data) => {
    console.log("onsubmit data", data);
    if (
      data.upgradeType === "default" &&
      urlOrLatest === "latest"
    ) {
      const commands = {
        command: "firmware-upgrade",
        version: data.urlOrLatest,
      };
      sendSiteCommands(
        {
          siteName: siteSelectedName,
          serials: selectedSerials,
          commands: commands,
          filter: {
            ...groupFilters,
            ...(groupSelectedName !== ""
              ? { dgname: groupSelectedName }
              : {}),
            ...(siteSelectedName !== ""
              ? { siteName: siteSelectedName }
              : {}),
          },
        },
        {
          onSuccess: async (response) => {
            if (response.status === "ok") {
              setUpgradeModalOpen(false);
              await refetch();
              setTableKey((prev) => prev + 1);
              reset();
              if (response?.jobid) {
                // navigate.push(`/jobDetails?jobID=${response?.jobid}`);
                setJobId(response?.jobid);
                setStepperOpen(true);
              }
            } else {
              setToastMessage(
                ` Upgrade command status: ${response.msg}`
              );
              setToastOpen(true);
            }
          },
          onError: (error) => {
            setToastMessage(
              `Error sending upgrade command: ${error.message}`
            );
            setToastOpen(true);
            setUpgradeModalOpen(false);
          },
        }
      );
    }
    if (
      data.upgradeType === "default" &&
      urlOrLatest === "url"
    ) {
      const commands = {
        command: "firmware-upgrade",
        url: data.url,
      };
      sendSiteCommands(
        {
          siteName: siteSelectedName,
          serials: selectedSerials,
          commands: commands,
          filter: {
            ...groupFilters,
            ...(groupSelectedName !== ""
              ? { dgname: groupSelectedName }
              : {}),
            ...(siteSelectedName !== ""
              ? { siteName: siteSelectedName }
              : {}),
          },
        },
        {
          onSuccess: async (response) => {
            if (response.status === "ok") {
              setUpgradeModalOpen(false);
              reset();
              await refetch();
              setTableKey((prev) => prev + 1);
              if (response?.jobid) {
                // navigate.push(
                //   `/jobDetails?jobID=${response?.jobid}`
                // );
                setJobId(response?.jobid);
                setStepperOpen(true);
              }
            } else {
              setToastMessage(
                ` Upgrade command status: ${response.msg}`
              );
              setToastOpen(true);
            }
          },
          onError: (error) => {
            setToastMessage(
              `Error sending upgrade command: ${error.message}`
            );
            setToastOpen(true);
            setUpgradeModalOpen(false);
          },
        }
      );
    }
    if (
      data.upgradeType === "rolling" &&
      urlOrLatest === "latest"
    ) {
      const commands = {
        command: "rolling-firmware-upgrade",
        operation: "add",
        version: data.urlOrLatest,
        start_time: data.startTime,
        upgrade_window: parseInt(data.operationWindow),
      };
      sendSiteCommands(
        {
          siteName: siteSelectedName,
          serials: selectedSerials,
          commands: commands,
          filter: {
            ...groupFilters,
            ...(groupSelectedName !== ""
              ? { dgname: groupSelectedName }
              : {}),
            ...(siteSelectedName !== ""
              ? { siteName: siteSelectedName }
              : {}),
          },
        },
        {
          onSuccess: async (response) => {
            if (response.status === "ok") {
              await refetch();
              setTableKey((prev) => prev + 1);
              setUpgradeModalOpen(false);
              reset();
              if (response?.jobid) {
                // navigate.push(
                //   `/jobDetails?jobID=${response?.jobid}`
                // );
                setJobId(response?.jobid);
                setStepperOpen(true);
              }
            } else {
              setToastMessage(
                ` Upgrade command status: ${response.msg}`
              );
              setToastOpen(true);
            }
          },
          onError: (error) => {
            setToastMessage(
              `Error sending upgrade command: ${error.message}`
            );
            setToastOpen(true);
            setUpgradeModalOpen(false);
          },
        }
      );
    }
    if (
      data.upgradeType === "rolling" &&
      urlOrLatest === "url"
    ) {
      const commands = {
        command: "rolling-firmware-upgrade",
        operation: "add",
        url: data.url,
        start_time: data.startTime,
        upgrade_window: isNaN(
          parseInt(data.operationWindow)
        )
          ? 0
          : parseInt(data.operationWindow),
      };
      sendSiteCommands(
        {
          siteName: siteSelectedName,
          serials: selectedSerials,
          commands: commands,
          filter: {
            ...groupFilters,
            ...(groupSelectedName !== ""
              ? { dgname: groupSelectedName }
              : {}),
            ...(siteSelectedName !== ""
              ? { siteName: siteSelectedName }
              : {}),
          },
        },
        {
          onSuccess: async (response) => {
            if (response.status === "ok") {
              await refetch();
              setTableKey((prev) => prev + 1);
              setUpgradeModalOpen(false);
              reset();
              if (response?.jobid) {
                // navigate.push(
                //   `/jobDetails?jobID=${response?.jobid}`
                // );
                setJobId(response?.jobid);
                setStepperOpen(true);
              }
            } else {
              setToastMessage(
                ` Upgrade command status: ${response.msg}`
              );
              setToastOpen(true);
            }
          },
          onError: (error) => {
            setToastMessage(
              `Error sending upgrade command: ${error.message}`
            );
            setToastOpen(true);
            setUpgradeModalOpen(false);
          },
        }
      );
    }
  };

  const upgradeType = watch("upgradeType");
  const urlOrLatest = watch("urlOrLatest");
  const operationWindow = watch("operationWindow");
  const startTime = watch("startTime");

  useEffect(() => {
    if (upgradeType === "default") {
      setValue("startTime", startTime);
      setValue("operationWindow", operationWindow);
    }
  }, [upgradeType]);

  return (
    <StyledDialog
      colors={colors}
      maxWidth={"md"}
      fullWidth
      open={upgradeModalOpen}
      hideBackdrop
    >
      <UpgradeMinersDialogHeader reset={reset} />
      <form
        style={{ overflow: "auto" }}
        onSubmit={handleSubmit(onsubmit)}
      >
        <Box
          display={"flex"}
          flexDirection={"column"}
          className="demand-config-modal-body"
          gap={4}
        >
          <Box
            display={"flex"}
            flexDirection={"column"}
            className="scheduling-decision-container"
            gap={4}
          >
            <H8TitleMedium>
              Select type of upgrade
            </H8TitleMedium>
            <Controller
              name="upgradeType"
              control={control}
              defaultValue={upgradeType}
              render={({ field }) => (
                <StyledToggleButtonGroup
                  exclusive
                  {...field}
                  colors={colors}
                  onChange={(e, value) =>
                    field.onChange(value)
                  }   
                  value={field.value}
                >
                  <StyledToggleButton
                    sx={{
                      textTransform: "none",
                      minWidth: "6rem",
                    }}  
                    colors={colors}
                    value={"default"}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: "0.5rem",
                        justifyContent: "center",
                        alignItems: "center",
                        flexGrow: 1,
                      }}
                    >
                      {field.value === "default" && (
                        <CheckIcon
                          sx={{ marginBottom: "3%"}}
                          fontSize="small"
                        />
                      )}
                      Default
                    </Box>
                  </StyledToggleButton>
                  <StyledToggleButton
                    sx={{
                      textTransform: "none",
                      minWidth: "6rem",
                    }}
                    value={"rolling"}
                    colors={colors}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: "0.5rem",
                        justifyContent: "center",
                        alignItems: "center",
                        flexGrow: 1,
                      }}
                    >
                      {field.value === "rolling" && (
                        <CheckIcon
                          sx={{ marginBottom: "3%"}}
                          fontSize="small"
                        />
                      )}
                      Rolling
                    </Box>
                  </StyledToggleButton>
                </StyledToggleButtonGroup>
              )}
            />
          </Box>
          {
            <Box
              display={"grid"}
              gridTemplateRows={"auto"}
              gap={7.76}
            >
              {upgradeType === "rolling" && (
                <Box
                  display={"grid"}
                  gridTemplateColumns={"1fr auto"}
                  gap={4}
                  alignContent={"center"}
                >
                  <Controller
                    name="startTime"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (value < dayjs()) {
                          return "Start time should be in the future";
                        }
                        if (
                          value < dayjs().add(5, "minute")
                        ) {
                          return "Please select a start time at least 5 minutes in the future.";
                        }
                        if (
                          value > dayjs().add(2, "month")
                        ) {
                          return "Start time should not be more than 2 months in the future";
                        }
                        if (
                          !dayjs(
                            value?.toDate
                              ? value.toDate()
                              : value
                          ).isValid()
                        ) {
                          return "Invalid date/time";
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <StyledFormControl colors={colors} >
                        <LocalizationProvider
                          dateAdapter={AdapterDayjs}
                        >
                          <DateTimePicker
                            label="Start Time"
                            value={field.value}
                            maxDate={dayjs().add(2, "month")}
                            disablePast={true}
                            slots={{
                              desktopPaper: StyledPaper,
                            }}
                            onChange={(date) =>
                              field.onChange(date)
                            }
                            slotProps={{
                              popper: {
                                sx: {
                                  "& .MuiPaper-root": {
                                    backgroundColor: colors?.background?.paper,
                                  },
                                },
                              },
                              desktopPaper: {
                                sx: {
                                  backgroundColor: colors?.background?.paper,
                                  borderRadius: "12px",
                                  boxShadow: "0px 4px 20px rgba(0,0,0,0.12)",
                            
                                  "& .MuiPickersCalendarHeader-label": {
                                    color: colors?.text?.primary,
                                  },
                            
                                  "& .MuiPickersCalendarHeader-switchViewIcon": {
                                    color: colors?.primary?.main,
                                    "&:hover": { color: colors?.primary?.dark },
                                  },
                            
                                  "& .MuiPickersArrowSwitcher-root .MuiIconButton-root": {
                                    color: colors?.primary?.main,
                                    "&:hover": { color: colors?.primary?.dark },
                                  },
                            
                                  "& .MuiDayCalendar-weekDayLabel": {
                                    color: colors?.text?.secondary,
                                  },
                            
                                  "& .MuiPickersDay-root": {
                                    color: colors?.text?.primary,
                                    "&:hover": {
                                      backgroundColor: colors?.hover?.muted,
                                    },
                                  },
                            
                                  "& .MuiPickersDay-root.Mui-disabled": {
                                    color: colors?.text?.muted,
                                  },
                            
                                  "& .MuiPickersDay-root.MuiPickersDay-today": {
                                    border: `1px solid ${colors?.text?.muted}`,
                                  },
                            
                                  "& .MuiPickersDay-root.Mui-selected": {
                                    backgroundColor: colors?.primary?.main,
                                    color: "#fff",
                                    borderRadius: "1rem",
                                    "&:hover": {
                                      backgroundColor: colors?.hover?.primary,
                                    },
                                  },
                            
                                  "& .MuiYearCalendar-root .MuiPickersYear-root": {
                                    color: colors?.text?.primary,
                                    "&:hover": {
                                      backgroundColor: colors?.hover?.muted,
                                    },
                                  },
                            
                                  "& .MuiYearCalendar-root .MuiPickersYear-yearButton.Mui-disabled": {
                                    color: colors?.text?.muted,
                                  },
                            
                                  "& .MuiYearCalendar-root .MuiPickersYear-root.Mui-selected": {
                                    backgroundColor: colors?.primary?.main,
                                    color: "#fff",
                                    "&:hover": {
                                      backgroundColor: colors?.hover?.primary,
                                    },
                                  },
                            
                                  "& .MuiPickersLayout-root .MuiPickersDigitalClock-root": {
                                    color: colors?.text?.primary,
                                  },
                            
                                  "& .MuiPickersLayout-root .Mui-selected": {
                                    backgroundColor: colors?.primary?.main,
                                    "&:hover": {
                                      backgroundColor: colors?.hover?.primary,
                                    },
                                  },
                            
                                  "& .MuiPickersLayout-root": {
                                    color: colors?.text?.muted,
                                  },
                            
                                  "& .MuiPickersToolbar-root .MuiButtonBase-root": {
                                    color: colors?.text?.primary,
                                    "&:hover": { color: colors?.primary?.main },
                                  },
                            
                                  "& .MuiPickersToolbar-root .Mui-selected": {
                                    backgroundColor: colors?.primary?.main,
                                    color: "#fff",
                                    "&:hover": {
                                      backgroundColor: colors?.hover?.primary,
                                    },
                                  },
                            
                                  "& .MuiDialogActions-root button": {
                                    color: colors?.primary?.main,
                                    fontWeight: 600,
                                    "&:hover": { color: colors?.primary?.dark },
                                  },
                                },
                              },
                              textField: {
                                error: Boolean(errors.startTime),
                                helperText: errors.startTime
                                  ? errors.startTime.message
                                  : "MM/DD/YYYY at HH:MM am/pm",
                            
                                sx: {
                                  "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": {
                                    color: colors?.text?.muted,
                                  },
                                  "& .MuiOutlinedInput-root .MuiOutlinedInput-input": {
                                    color: colors?.text?.muted, // date/time value
                                  },
                                  "& .MuiOutlinedInput-root .MuiInputAdornment-root, & .MuiSvgIcon-root": {
                                    color: colors?.text?.muted, // icons / AM-PM adornments
                                    "&:hover": { color: colors?.primary?.main },
                                  },
                                  "& .MuiFormHelperText-root": {
                                    color: colors?.text?.muted,
                                  },
                                },
                                inputProps: {
                                  style: { color: colors?.text?.primary },
                                  placeholder: "MM/DD/YYYY at HH:MM am/pm",
                                },
                              },
                              day: {
                                sx: {
                                  "&.MuiPickersDay-root.Mui-selected": {
                                    backgroundColor: colors.primary.main,
                                    "&:hover": {
                                      backgroundColor: colors.hover?.primary,
                                    },
                                  },
                                  "&.Mui-disabled": {
                                    WebkitTextFillColor: `${colors?.text?.hint} !important`,
                                  },
                                },
                              },
                              digitalClockSectionItem: {
                                sx: {
                                  "&.Mui-selected": {
                                    backgroundColor: colors.primary.main,
                                    borderRadius: "50%",
                                    "&:hover": {
                                      backgroundColor: colors.hover?.primary,
                                    },
                                  },
                                  "&:hover": {
                                    backgroundColor: colors.hover?.muted,
                                  },
                                },
                              },
                            }}
                            
                            renderInput={(props) => (
                              <StyledTextField colors={colors}
                                {...props}
                                variant="outlined"
                                error={Boolean(
                                  errors.startTime
                                )}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </StyledFormControl>
                    )}
                  />
                  <Box
                    display={"grid"}
                    alignItems={"center"}
                  >
                    <SecondaryButton colors={colors}
                      onClick={async () => {
                        const newStart = dayjs().add(
                          6,
                          "minute"
                        );
                        await setValue(
                          "startTime",
                          newStart,
                          { shouldValidate: true }
                        );
                        clearErrors(["startTime"]);
                      }}
                    >
                      Reset time
                    </SecondaryButton>
                  </Box>
                </Box>
              )}
              {upgradeType === "rolling" && (
                <Box
                  display={"grid"}
                  gridTemplateRows={"auto"}
                  gap={4}
                >
                  <H8TitleMedium>
                    Upgrade operation window
                  </H8TitleMedium>
                  <Controller
                    name="operationWindow"
                    control={control}
                    rules={{
                      required:
                        "Operation window is required",
                      min: {
                        value: 120,
                        message:
                          "Operation window should be at least 120 seconds",
                      },
                      max: {
                        value: 43200,
                        message:
                          "Operation window should not exceed 43200 seconds (12 hours)",
                      },
                    }}
                    render={({ field }) => (
                      <StyledTextField colors={colors}
                        margin="none"
                        variant="outlined"
                        type="number"
                        fullWidth
                        label="Operation window"
                        {...field}
                        error={Boolean(
                          errors.operationWindow
                        )}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value);
                        }}
                        helperText={
                          errors.operationWindow
                            ? errors.operationWindow.message
                            : "Enter a number between 120 and 43200 seconds (12 hours)"
                        }
                      />
                    )}
                  />
                </Box>
              )}
              <Box
                display="flex"
                flexDirection={"column"}
                gap={4}
              >
                <H8TitleMedium>
                  Select an option to upgrade
                </H8TitleMedium>

                <StyledFormControl colors={colors} component="fieldset">
                  <Controller
                    name="urlOrLatest"
                    control={control}
                    rules={{
                      required: "Please select an option",
                    }}
                    render={({
                      field,
                      fieldState: { error },
                    }) => (
                      <>
                        <RadioGroup {...field}>
                          <FormControlLabel
                            value="latest"
                            control={<StyledRadio colors={colors} />}
                            label="Latest"
                            sx={{ color: colors.text.primary }}
                          />
                          <FormControlLabel
                            value="url"
                            control={<StyledRadio colors={colors} />}
                            label="URL"
                            sx={{ color: colors.text.primary }}
                          />
                        </RadioGroup>
                      </>
                    )}
                  />
                </StyledFormControl>
              </Box>
              {urlOrLatest === "url" && (
                <Box
                  display={"grid"}
                  gridTemplateRows={"auto"}
                  gap={4}
                >
                  <H8TitleMedium>Url</H8TitleMedium>
                  <Controller
                    name="url"
                    control={control}
                    rules={{
                      required: "Please enter a url",
                      pattern: {
                        value:
                          /^(ftp|http|https):\/\/[^ "]+$/,
                        message: "Please enter a valid url",
                      },
                    }}
                    render={({ field }) => (
                      <StyledTextField colors={colors}
                        margin="none"
                        variant="outlined"
                        label="URL"
                        {...field}
                        error={Boolean(errors.url)}
                        helperText={
                          errors.url
                            ? errors.url.message
                            : "Enter a valid url for firmware upgrade"
                        }
                      />
                    )}
                  />
                </Box>
              )}
              {upgradeType === "default" && (
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  flexGrow={1}
                  flexWrap={"wrap"}
                  gap={2}
                >
                  <BodyMedium>
                    <b>Important:</b> To ensure stability,
                    upgrade and restart one miner initially
                    before applying the update to the entire
                    site or group. Exercise caution when
                    upgrading the entire site or group.
                  </BodyMedium>
                  <BodyMedium>
                    <b>NOTE:</b> Before upgrading all
                    miners, please ensure to upgrade one
                    miner initially and check its status in
                    the last job op column of Miner tab. On
                    success, restart miner and verify its
                    status in the last job op column of
                    Miner tab.
                  </BodyMedium>
                </Box>
              )}
              {upgradeType === "rolling" && (
                <BodyMedium>
                  <b>Note:</b> This operation requires miner
                  firmware v2.0.0 or later.
                </BodyMedium>
              )}
            </Box>
          }
        </Box>
        <DialogActions sx={{ paddingTop: "1rem" }}>
          <PrimaryButton colors={colors} disabled={!isValid} type="submit">
            Schedule
          </PrimaryButton>
        </DialogActions>
      </form>
    </StyledDialog>
  );
};

const UpgradeMinersDialog = (props) => {
  const { refetch, setTableKey } = props;
  return (
    <>
      <UpgradeMinersModal
        refetch={refetch}
        setTableKey={setTableKey}
      />
    </>
  );
};

export default UpgradeMinersDialog;
